// File: components/SpicyChallenge.tsx
import React, { useState, useRef, useEffect } from 'react';
import { PlayerProfile, HuntrixMember, IdolRank } from '../types';
import { ExitButton } from './ExitButton';
import { Flame, Skull, Trophy, AlertTriangle, Play, Utensils } from 'lucide-react';
import { MusicManager } from './audio/GameAudioManager';
import * as Tone from 'tone';
// FIX: Rename Notification import to avoid collision with global browser Notification API
import { Notification as GameNotification } from './NotificationSystem';
import { RANKS } from '../constants';

// FIX: Update interface to use renamed GameNotification
interface SpicyChallengeProps {
    player: PlayerProfile;
    onUpdatePlayer: (updates: Partial<PlayerProfile>) => void;
    onExit: () => void;
    onNotify: (message: string, type?: GameNotification['type']) => void;
}

const ROUNDS = [
    { name: "MILD JALAPEÑO", scoville: 2000, damage: 10, reward: 5000 },
    { name: "SPICY CAYENNE", scoville: 30000, damage: 15, reward: 10000 },
    { name: "HOT HABANERO", scoville: 200000, damage: 25, reward: 25000 },
    { name: "GHOST PEPPER", scoville: 1000000, damage: 35, reward: 50000 },
    { name: "CAROLINA REAPER", scoville: 2200000, damage: 50, reward: 100000 }
];

// --- CUSTOM RENDERER FOR NOODLE SCENE ---
const drawNoodleEater = (
    ctx: CanvasRenderingContext2D,
    memberId: string,
    heatLevel: number, // 0 to 4
    time: number
) => {
    ctx.save();

    // 1. CONFIGURATION BASED ON MEMBER
    let hairColor = "#333";
    let hairHighlight = "#555";
    let outfitColor = "#333";
    let outfitDetail = "#555";
    let skinTone = "#ffdbac";
    let cupColor = "#fff";
    let cupTextColor = "#000";

    if (memberId === 'RUMI') {
        hairColor = "#8e44ad"; // Purple
        hairHighlight = "#a569bd";
        outfitColor = "#d4ac0d"; // Yellow Jacket
        outfitDetail = "#b9770e"; // Darker Yellow/Gold shadow
        cupColor = "#2e1065"; // Dark Purple
        cupTextColor = "#facc15"; // Yellow Text
    } else if (memberId === 'MIRA') {
        hairColor = "#db2777"; // Pink
        hairHighlight = "#f472b6";
        outfitColor = "#111827"; // Black Top
        outfitDetail = "#374151"; // Grey detail
        cupColor = "#be185d"; // Pink/Red
        cupTextColor = "#fff";
    } else if (memberId === 'ZOEY') {
        hairColor = "#0f172a"; // Dark Blue/Black
        hairHighlight = "#334155";
        outfitColor = "#0891b2"; // Cyan/Teal
        outfitDetail = "#164e63";
        cupColor = "#0e7490"; // Cyan
        cupTextColor = "#ecfeff";
    }

    // 2. SCALING & POSITION
    ctx.scale(1.6, 1.6); // Reduced from 2.2 to fix initial zoom issue
    const bob = Math.sin(time / 150) * 1.5;
    const shake = Math.sin(time / 40) * (heatLevel * 0.5); // Shakes when spicy
    ctx.translate(shake, bob);

    // --- HAIR BACK (Braid for Rumi, Pigtails/Flowing for others) ---
    ctx.fillStyle = hairColor;
    if (memberId === 'RUMI') {
        const braidSegments = 14;
        for (let i = 0; i < braidSegments; i++) {
            const yPos = -95 + (i * 20);
            const w = 44 - (i * 1.5);
            const h = 32;
            const xSway = Math.sin(time / 400 + i * 0.4) * (i * 2.5);
            ctx.fillStyle = hairColor;
            ctx.beginPath();
            ctx.ellipse(xSway, yPos, w, h, 0, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = hairHighlight;
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.moveTo(xSway - w / 2 + 8, yPos - 10);
            ctx.quadraticCurveTo(xSway, yPos - 20, xSway + w / 2 - 8, yPos - 10);
            ctx.stroke();
        }
        ctx.fillStyle = "#ffd700";
        ctx.beginPath(); ctx.arc(0, -95, 12, 0, Math.PI * 2); ctx.fill();

        // Sideburns - strands of hair framing the face
        ctx.fillStyle = hairColor;
        // Left sideburn
        ctx.beginPath();
        ctx.moveTo(-45, -60);
        ctx.quadraticCurveTo(-55, -30, -50, 10);
        ctx.quadraticCurveTo(-48, 20, -42, 15);
        ctx.quadraticCurveTo(-45, -20, -40, -55);
        ctx.closePath();
        ctx.fill();
        // Right sideburn
        ctx.beginPath();
        ctx.moveTo(45, -60);
        ctx.quadraticCurveTo(55, -30, 50, 10);
        ctx.quadraticCurveTo(48, 20, 42, 15);
        ctx.quadraticCurveTo(45, -20, 40, -55);
        ctx.closePath();
        ctx.fill();
    } else if (memberId === 'MIRA') {
        // Mira's UNIFIED long flowing hair - narrower at crown, knee-length
        const hairSway = Math.sin(time / 400) * 3;
        ctx.beginPath();
        // Start at top center of head (narrower)
        ctx.moveTo(0, -88);
        // Crown hugs head more tightly
        ctx.quadraticCurveTo(40, -85, 50, -50);
        // Transition to flowing hair
        ctx.quadraticCurveTo(60, -20, 65 + hairSway, 30);
        // Right side flows down to knees (shortened from 180 to 140)
        ctx.bezierCurveTo(68 + hairSway, 70, 58 + hairSway, 110, 45 + hairSway, 140);
        // Bottom right tip
        ctx.quadraticCurveTo(30 + hairSway, 155, 12, 145 + hairSway);
        // Inner curve at bottom
        ctx.quadraticCurveTo(0, 135 + hairSway, -12, 145 + hairSway);
        // Bottom left tip
        ctx.quadraticCurveTo(-30 + hairSway, 155, -45 + hairSway, 140);
        // Left side flows up
        ctx.bezierCurveTo(-58 + hairSway, 110, -68 + hairSway, 70, -65 + hairSway, 30);
        // Transition back to crown
        ctx.quadraticCurveTo(-60, -20, -50, -50);
        // Crown curves back to top (narrower)
        ctx.quadraticCurveTo(-40, -85, 0, -88);
        ctx.closePath();
        ctx.fill();
    }

    // --- BODY & OUTFIT ---
    ctx.fillStyle = outfitColor;

    // Shoulders - Broad and rounded
    ctx.beginPath();
    ctx.ellipse(0, 90, 60, 45, 0, Math.PI, 0);
    ctx.fill();

    // Outfit Details
    if (memberId === 'RUMI') {
        ctx.fillStyle = "#111"; ctx.fillRect(-20, 50, 40, 40);
        ctx.fillStyle = outfitDetail;
        ctx.beginPath(); ctx.moveTo(-50, 55); ctx.lineTo(-20, 90); ctx.lineTo(-60, 90); ctx.fill();
        ctx.beginPath(); ctx.moveTo(50, 55); ctx.lineTo(20, 90); ctx.lineTo(60, 90); ctx.fill();
        ctx.fillStyle = outfitColor;
        ctx.beginPath(); ctx.moveTo(-35, 50); ctx.lineTo(-15, 80); ctx.lineTo(-50, 80); ctx.closePath(); ctx.fill();
        ctx.beginPath(); ctx.moveTo(35, 50); ctx.lineTo(15, 80); ctx.lineTo(50, 80); ctx.closePath(); ctx.fill();
        ctx.fillStyle = "#ef4444"; ctx.fillRect(-55, 65, 10, 10);
        ctx.fillStyle = "#3b82f6"; ctx.beginPath(); ctx.arc(55, 70, 6, 0, Math.PI * 2); ctx.fill();
    } else if (memberId === 'MIRA') {
        ctx.fillStyle = "#fff"; ctx.font = "bold 6px Arial"; ctx.textAlign = "center";
        ctx.globalAlpha = 0.6; ctx.fillText("NO", 0, 75); ctx.fillText("LIMIT", 0, 82); ctx.globalAlpha = 1.0;
        ctx.fillStyle = "#000"; ctx.fillRect(-12, 45, 24, 4);
        ctx.fillStyle = "#facc15"; ctx.beginPath(); ctx.arc(0, 47, 2, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#333"; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(-30, 50); ctx.lineTo(-30, 90); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(30, 50); ctx.lineTo(30, 90); ctx.stroke();
    } else if (memberId === 'ZOEY') {
        ctx.fillStyle = "#111"; ctx.fillRect(-35, 50, 6, 40); ctx.fillRect(29, 50, 6, 40);
        ctx.strokeStyle = "rgba(255,255,255,0.2)"; ctx.lineWidth = 1;
        ctx.beginPath(); for (let i = 0; i < 5; i++) { ctx.moveTo(-20 + i * 10, 60); ctx.lineTo(-20 + i * 10, 90); } ctx.stroke();
    }

    // --- NECK ---
    ctx.fillStyle = skinTone;
    ctx.fillRect(-12, 35, 24, 20);
    ctx.fillStyle = "rgba(0,0,0,0.1)";
    ctx.fillRect(-12, 35, 24, 5);

    // --- HAIR BACK MASS (Zoey only, Rumi/Mira already handled) ---
    if (memberId === 'ZOEY') {
        ctx.fillStyle = hairColor;
        ctx.beginPath(); ctx.arc(0, -25, 65, Math.PI, 0); ctx.fill();
        ctx.fillRect(-65, -25, 130, 65);
    }

    // --- FACE ---
    ctx.fillStyle = skinTone;
    ctx.beginPath();
    ctx.moveTo(-45, -40);
    ctx.quadraticCurveTo(-65, 30, -25, 60);
    ctx.quadraticCurveTo(0, 65, 25, 60);
    ctx.quadraticCurveTo(65, 30, 45, -40);
    ctx.quadraticCurveTo(45, -80, 0, -85);
    ctx.quadraticCurveTo(-45, -80, -45, -40);
    ctx.fill();

    if (heatLevel > 0) {
        const flushAlpha = Math.min(0.7, heatLevel * 0.2);
        const grad = ctx.createRadialGradient(0, 10, 10, 0, 10, 50);
        grad.addColorStop(0, `rgba(220, 38, 38, ${flushAlpha})`);
        grad.addColorStop(1, "rgba(220, 38, 38, 0)");
        ctx.fillStyle = grad;
        ctx.fill();
    }

    ctx.fillStyle = skinTone;
    ctx.beginPath(); ctx.ellipse(-52, -5, 8, 14, -0.1, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(52, -5, 8, 14, 0.1, 0, Math.PI * 2); ctx.fill();
    if (memberId === 'RUMI') {
        ctx.fillStyle = "#ffd700"; ctx.beginPath(); ctx.arc(-52, 5, 4, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(52, 5, 4, 0, Math.PI * 2); ctx.fill();
    } else if (memberId === 'ZOEY') {
        ctx.fillStyle = "#c0c0c0"; ctx.beginPath(); ctx.arc(-52, 2, 3, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(52, 2, 3, 0, Math.PI * 2); ctx.fill();
    }

    const drawEye = (x: number) => {
        ctx.save();
        ctx.translate(x, -10);
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        const eyeSize = 16 + (heatLevel * 2);
        ctx.ellipse(0, 0, eyeSize, eyeSize * 0.9, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = 1; ctx.strokeStyle = "#000"; ctx.stroke();
        ctx.fillStyle = (memberId === 'RUMI' ? "#5b21b6" : (memberId === 'MIRA' ? "#be185d" : "#0e7490"));
        const irisSize = Math.max(3, 7 - heatLevel);
        ctx.beginPath(); ctx.arc(0, 0, irisSize, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#000"; ctx.beginPath(); ctx.arc(0, 0, irisSize * 0.4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(3, -3, 2, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "#000"; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(-eyeSize, -5); ctx.quadraticCurveTo(0, -eyeSize - 5, eyeSize, -5); ctx.stroke();
        ctx.lineWidth = 3; ctx.strokeStyle = hairColor;
        const browOffset = -eyeSize - 15 - (heatLevel * 3);
        ctx.beginPath();
        if (memberId === 'ZOEY') { ctx.moveTo(-10, browOffset); ctx.lineTo(15, browOffset - 5); }
        else { ctx.moveTo(-15, browOffset); ctx.lineTo(15, browOffset + 5); }
        ctx.stroke();
        ctx.restore();
    };
    drawEye(-22); drawEye(22);

    ctx.fillStyle = "rgba(0,0,0,0.2)";
    ctx.beginPath(); ctx.arc(0, 15, 2, 0, Math.PI * 2); ctx.fill();

    // MOUTH (Base lips)
    ctx.save();
    ctx.translate(0, 35);
    ctx.fillStyle = "#fda4af";
    ctx.beginPath(); ctx.ellipse(0, 0, 12, 8, 0, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "#450a0a";
    ctx.beginPath(); ctx.ellipse(0, 0, 6, 4, 0, 0, Math.PI * 2); ctx.fill();
    ctx.restore();

    // HAIR FRONT
    ctx.save();
    ctx.fillStyle = hairColor;
    ctx.beginPath();
    if (memberId === 'ZOEY') {
        ctx.moveTo(-65, -60); ctx.lineTo(-45, -35); ctx.lineTo(-20, -32); ctx.lineTo(0, -30);
        ctx.lineTo(20, -32); ctx.lineTo(45, -35); ctx.lineTo(65, -60);
        ctx.quadraticCurveTo(60, -90, 0, -95); ctx.quadraticCurveTo(-60, -90, -65, -60);
    } else if (memberId === 'MIRA') {
        ctx.moveTo(-60, -50); ctx.quadraticCurveTo(-20, -40, -5, -35); ctx.lineTo(0, -45); ctx.lineTo(5, -35);
        ctx.quadraticCurveTo(20, -40, 60, -50);
        ctx.quadraticCurveTo(60, -90, 0, -95); ctx.quadraticCurveTo(-60, -90, -60, -50);
    } else {
        ctx.moveTo(-60, -55); ctx.quadraticCurveTo(0, -35, 60, -55); ctx.quadraticCurveTo(60, -95, 0, -100);
        ctx.quadraticCurveTo(-60, -95, -60, -55);
    }
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.2)"; ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(-30, -70); ctx.quadraticCurveTo(0, -60, 30, -70); ctx.stroke();
    ctx.restore();

    // --- CUP BACK (The Rim behind noodles) ---
    ctx.save();
    ctx.translate(0, 90);
    ctx.fillStyle = "#e5e5e5";
    ctx.beginPath(); ctx.ellipse(0, -30, 40, 8, 0, 0, Math.PI * 2); ctx.fill();
    // Front Rim Edge (Thin gray line representing top of lid)
    ctx.strokeStyle = "#d4d4d4"; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.ellipse(0, -30, 40, 8, 0, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();


    // --- NOODLES (Drawn AFTER Cup Back to be inside) ---
    ctx.save();
    ctx.translate(0, 35);
    ctx.strokeStyle = "#fef9c3"; ctx.lineWidth = 4; ctx.lineCap = "round";
    for (let i = 0; i < 8; i++) {
        const offset = (i - 3.5) * 4;
        const wiggle = Math.sin(time / 80 + i) * 3;
        ctx.beginPath();
        ctx.moveTo(offset, 0);
        // Ends inside the cup (Cup is at Y=90 relative to head, so Y=55+ is inside rim area)
        ctx.quadraticCurveTo(offset + wiggle, 40, 0, 75);
        ctx.stroke();
    }
    ctx.restore();

    // --- CUP FRONT (The body and label) ---
    ctx.save();
    ctx.translate(0, 90);
    // Hands
    ctx.fillStyle = skinTone;
    ctx.beginPath(); ctx.ellipse(-45, 0, 18, 22, -0.3, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.ellipse(45, 0, 18, 22, 0.3, 0, Math.PI * 2); ctx.fill();
    // Body
    ctx.fillStyle = cupColor;
    ctx.beginPath(); ctx.moveTo(-40, -30); ctx.lineTo(40, -30); ctx.lineTo(30, 60); ctx.lineTo(-30, 60); ctx.closePath(); ctx.fill();

    // Label
    ctx.save();
    ctx.clip(new Path2D("M -40 -30 L 40 -30 L 30 60 L -30 60 Z"));
    ctx.fillStyle = "rgba(255,255,255,0.1)"; ctx.fillRect(-40, -10, 80, 40);
    ctx.fillStyle = cupTextColor; ctx.font = "900 24px 'Impact', sans-serif";
    ctx.textAlign = "center"; ctx.fillText(memberId, 0, 15);
    ctx.fillStyle = "#fff"; ctx.font = "bold 8px sans-serif";
    const flavor = memberId === 'RUMI' ? "SUPERSTAR FLAVOR" : (memberId === 'MIRA' ? "SPICE QUEEN" : "HAMBURGER");
    ctx.fillText(flavor, 0, 25); ctx.font = "16px sans-serif"; ctx.fillText("🌶️", 0, -5);
    ctx.restore();

    // Chopsticks
    ctx.save(); ctx.translate(50, -20); ctx.rotate(-0.3); ctx.fillStyle = "#8d6e63"; ctx.fillRect(0, 0, 4, 120);
    ctx.rotate(0.15); ctx.fillRect(10, 0, 4, 120); ctx.restore();
    ctx.restore();

    // PARTICLES
    ctx.strokeStyle = "rgba(255, 255, 255, 0.4)"; ctx.lineWidth = 4; ctx.lineCap = "round";
    const steamY = (time / 15) % 30;
    ctx.beginPath(); ctx.moveTo(-15, -40); ctx.quadraticCurveTo(-10, -70 - steamY, -20, -100 - steamY); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(15, -40); ctx.quadraticCurveTo(20, -70 - steamY, 10, -100 - steamY); ctx.stroke();

    if (heatLevel >= 3) {
        ctx.fillStyle = "rgba(200, 240, 255, 0.8)";
        const sweatX = Math.sin(time / 50) * 60;
        ctx.beginPath(); ctx.arc(sweatX, -80, 3, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(-sweatX, -90, 2, 0, Math.PI * 2); ctx.fill();
    }
    ctx.restore();
};

export const SpicyChallenge: React.FC<SpicyChallengeProps> = ({ player, onUpdatePlayer, onExit, onNotify }) => {
    const [phase, setPhase] = useState<'SELECT' | 'EATING' | 'RESULT' | 'GAMEOVER'>('SELECT');
    const [selectedMemberId, setSelectedMemberId] = useState<string>(player.group.members[0].id);
    const [currentRound, setCurrentRound] = useState(0);
    const [totalReward, setTotalReward] = useState(0);
    const [message, setMessage] = useState("");
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const selectedMember = player.group.members.find(m => m.id === selectedMemberId)!;
    const rankData = RANKS[player.rank] || RANKS[IdolRank.TRAINEE];
    const multiplier = rankData?.multiplier || 1;

    const playCrunch = () => {
        if (!MusicManager.ctx || Tone.context.state !== 'running') return;
        MusicManager.playSFX("noiseSynth", "16n");
    };

    const playGulp = () => {
        if (!MusicManager.ctx || Tone.context.state !== 'running') return;
        MusicManager.playSFX("kick", "C2", "16n");
    };

    const playWin = () => {
        if (!MusicManager.ctx || Tone.context.state !== 'running') return;
        MusicManager.playSFX("uiSynth", ["C4", "E4", "G4", "C5"], "8n");
    };

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const rect = canvas.getBoundingClientRect();
        if (canvas.width !== rect.width * dpr || canvas.height !== rect.height * dpr) {
            canvas.width = rect.width * dpr; canvas.height = rect.height * dpr;
        }
        let animId: number;
        const animate = () => {
            const time = Date.now(); ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.save(); ctx.scale(dpr, dpr);
            const bgGrad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
            bgGrad.addColorStop(0, "#2e1065"); bgGrad.addColorStop(0.5, "#4c1d95"); bgGrad.addColorStop(1, "#831843");
            ctx.fillStyle = bgGrad; ctx.fillRect(0, 0, rect.width, rect.height);
            ctx.strokeStyle = "rgba(236, 72, 153, 0.3)"; ctx.lineWidth = 2;
            for (let i = 0; i < 5; i++) {
                const x = (time / 50 + i * 100) % rect.width;
                ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x - 50, rect.height); ctx.stroke();
            }
            const centerX = rect.width / 2; const centerY = rect.height * 0.18; // Moved up from 0.22
            const shakeIntensity = phase === 'EATING' ? (currentRound * 1.5) : 0;
            const ox = (Math.random() - 0.5) * shakeIntensity; const oy = (Math.random() - 0.5) * shakeIntensity;
            ctx.translate(centerX + ox, centerY + oy);
            drawNoodleEater(ctx, selectedMemberId, currentRound, time);
            ctx.restore();
            animId = requestAnimationFrame(animate);
        };
        animate(); return () => cancelAnimationFrame(animId);
    }, [selectedMemberId, currentRound, phase]);

    const handleSlurp = async () => {
        await MusicManager.initialize(); playCrunch();
        const roundData = ROUNDS[currentRound];
        const mitigation = selectedMember.style / 200;
        const actualDamage = Math.floor(roundData.damage * (1 - mitigation));
        const newSpirit = selectedMember.spirit - actualDamage;
        if (newSpirit <= 0) {
            const updatedMembers = player.group.members.map(m => m.id === selectedMemberId ? { ...m, spirit: 0 } : m);
            onUpdatePlayer({ group: { ...player.group, members: updatedMembers } });
            setPhase('GAMEOVER'); setMessage(`${selectedMember.name} passed out from the heat! No rewards.`);
        } else {
            playGulp();
            const updatedMembers = player.group.members.map(m => m.id === selectedMemberId ? { ...m, spirit: newSpirit } : m);
            onUpdatePlayer({ group: { ...player.group, members: updatedMembers } });
            setTotalReward(prev => prev + (roundData.reward * multiplier));
            if (currentRound < ROUNDS.length - 1) setCurrentRound(prev => prev + 1);
            else { playWin(); finishGame(true); }
        }
    };

    const finishGame = (completed: boolean) => {
        setPhase('RESULT');
        if (completed) {
            setMessage("CHALLENGE CLEARED! THE CROWD GOES WILD!");
            const updatedMembers = player.group.members.map(m => m.id === selectedMemberId ? { ...m, style: Math.min(99, m.style + 5) } : m);
            onUpdatePlayer({ fans: player.fans + totalReward, group: { ...player.group, members: updatedMembers } });
        } else {
            setMessage("You tapped out, but kept your dignity.");
            onUpdatePlayer({ fans: player.fans + totalReward });
        }
    };

    const reset = () => { setPhase('SELECT'); setCurrentRound(0); setTotalReward(0); setMessage(""); };

    return (
        <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-8 relative overflow-hidden">
            <ExitButton onExit={onExit} />
            <div className="relative z-10 w-full max-w-5xl glass-panel p-0 rounded-2xl border-4 border-pink-500/50 shadow-[0_0_50px_rgba(236,72,153,0.3)] flex flex-col h-full overflow-hidden">
                <header className="absolute top-0 left-0 w-full p-6 z-20 flex justify-between items-start pointer-events-none">
                    <div className="bg-black/60 backdrop-blur-sm p-3 rounded-lg border border-pink-500/30">
                        <div className="flex items-center gap-2 mb-0.5">
                            <Flame className="text-orange-500 animate-pulse" size={18} />
                            <h2 className="text-xl font-heading text-white italic tracking-tighter drop-shadow-lg">SPICY CHALLENGE</h2>
                        </div>
                        <div className="text-pink-300 font-bold tracking-widest text-[8px] uppercase">Broadcast Live from Ramyeon Kitchen</div>
                    </div>
                    {phase !== 'SELECT' && (
                        <div className="bg-black/60 backdrop-blur-sm px-4 py-2 rounded-lg border border-red-500/30 text-center">
                            <div className="text-[8px] text-red-400 font-bold tracking-widest mb-0.5">CURRENT HEAT</div>
                            <div className="text-lg font-black text-white uppercase">{ROUNDS[currentRound].name}</div>
                            <div className="text-[10px] text-orange-400 font-mono">{ROUNDS[currentRound].scoville.toLocaleString()} SHU</div>
                        </div>
                    )}
                </header>
                <div className="relative w-full h-full bg-slate-900">
                    <canvas ref={canvasRef} className="w-full h-full object-cover" />
                    {selectedMember.spirit <= 0 && (
                        <div className="absolute inset-0 bg-black/80 flex items-center justify-center z-10">
                            <Skull size={120} className="text-red-500 animate-pulse" />
                        </div>
                    )}
                </div>
                <div className="absolute bottom-0 left-0 w-full p-4 z-20 flex justify-center items-end pointer-events-none">
                    <div className="bg-black/80 backdrop-blur-md p-4 rounded-2xl border border-slate-700 pointer-events-auto w-full max-w-xl shadow-2xl">
                        {phase === 'SELECT' && (
                            <div className="text-center space-y-4">
                                <p className="text-lg text-slate-300">Can your idol handle the heat? <br /><span className="text-sm text-slate-400">Select a member to attempt the 5-Course Death Noodle Challenge.</span></p>
                                <div className="grid grid-cols-3 gap-4">
                                    {player.group.members.map(m => (
                                        <button key={m.id} onClick={() => setSelectedMemberId(m.id)} className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${selectedMemberId === m.id ? 'bg-pink-600 border-white text-white scale-105 shadow-xl' : 'bg-slate-800 border-slate-600 text-slate-400 hover:bg-slate-700'}`}>
                                            <span className="font-heading text-lg">{m.name}</span>
                                            <div className="w-full flex flex-col gap-1">
                                                <div className="flex justify-between text-[10px] font-bold uppercase w-full text-slate-300"><span>Spirit</span><span>{m.spirit}/{m.maxSpirit}</span></div>
                                                <div className="w-full h-2 bg-black/50 rounded-full overflow-hidden border border-white/10"><div className={`h-full transition-all duration-300 ${m.spirit < 30 ? 'bg-red-500 animate-pulse' : 'bg-green-400'}`} style={{ width: `${(m.spirit / m.maxSpirit) * 100}%` }}></div></div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <button onClick={() => { if (selectedMember.spirit > 20) setPhase('EATING'); else onNotify("Spirit too low to start!", 'error'); }} className="w-full py-4 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-500 hover:to-orange-500 text-white font-heading text-2xl rounded-xl shadow-[0_0_30px_#dc2626] transition-transform hover:scale-[1.02] flex items-center justify-center gap-3"><Utensils /> START MUKBANG</button>
                            </div>
                        )}
                        {phase === 'EATING' && (
                            <div className="flex flex-col gap-1 w-full">
                                <div className="flex items-center justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="text-xl font-heading text-white mb-0 drop-shadow-md">{selectedMember.name}</h3>
                                            <div className="text-[8px] text-slate-400 font-bold uppercase tracking-widest drop-shadow-md">SPIRIT</div>
                                        </div>
                                        <div className="w-full h-3 bg-slate-900/50 rounded-full overflow-hidden border border-slate-500/50 relative shadow-inner">
                                            <div className={`h-full transition-all duration-300 ease-out ${selectedMember.spirit < 30 ? 'bg-red-500 animate-pulse' : 'bg-green-500'}`} style={{ width: `${Math.max(0, (selectedMember.spirit / selectedMember.maxSpirit) * 100)}%` }} />
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-sm font-mono text-white tracking-widest drop-shadow-md">{selectedMember.spirit}/{selectedMember.maxSpirit}</div>
                                        <div className="text-[10px] text-red-400 font-bold">-{ROUNDS[currentRound].damage} RISK</div>
                                    </div>
                                </div>

                                <div className="flex gap-4 mt-1">
                                    <button onClick={handleSlurp} className="flex-[2] py-3 bg-gradient-to-r from-red-700 to-pink-600 hover:from-red-600 hover:to-pink-500 text-white font-heading text-2xl rounded-xl border-b-4 border-red-900 active:border-b-0 active:translate-y-1 transition-all shadow-xl flex items-center justify-center gap-3 group">
                                        <Utensils className="group-hover:rotate-12 transition-transform w-5 h-5" />
                                        SLURP!
                                    </button>
                                    <button onClick={() => finishGame(false)} className="flex-1 py-3 bg-slate-800 hover:bg-slate-700 text-slate-400 font-bold rounded-xl border-2 border-slate-600 hover:text-white transition-colors text-xs text-center flex items-center justify-center">
                                        TAP OUT
                                    </button>
                                </div>
                                <div className="text-center text-[9px] text-green-400 font-bold tracking-widest uppercase">Reward: +{(ROUNDS[currentRound].reward * multiplier).toLocaleString()} Fans</div>
                            </div>
                        )}
                        {(phase === 'RESULT' || phase === 'GAMEOVER') && (
                            <div className="text-center space-y-6">
                                <div><h3 className={`text-4xl font-heading mb-2 ${phase === 'GAMEOVER' ? 'text-red-500' : 'text-green-400'}`}>{phase === 'GAMEOVER' ? 'PASSED OUT!' : 'CLEARED!'}</h3><p className="text-lg text-white">{message}</p></div>
                                {phase === 'RESULT' && (<div className="bg-green-900/30 border border-green-500/50 p-4 rounded-lg inline-block w-full"><div className="text-sm text-green-300 uppercase font-bold">Total Earnings</div><div className="text-4xl font-mono text-white">+{totalReward.toLocaleString()} Fans</div></div>)}
                                <button onClick={reset} className="w-full py-4 bg-white text-black font-bold rounded-xl hover:bg-slate-200 transition-colors flex items-center justify-center gap-2"><Play size={20} /> PLAY AGAIN</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
